import { Check, ChevronLeft, ChevronRight, Copy, Download } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Button } from '~/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/utils/cn';
import { triggerBrowserDownload } from '~/utils/download-file';
import { extractApiError } from '~/utils/extract-api-error';

import type { ExportPreviewSource } from '../../hooks/use-export-template-preview';
import { useExportTemplatePreview } from '../../hooks/use-export-template-preview';
import type {
  ExportFormatOption,
  ExportTemplateColumn,
  ExportTemplateDefinition,
  ExportTransformation,
} from '../../types/export.types';
import {
  appliedTransformations,
  buildPreviewCells,
  previewToDelimitedText,
} from '../../utils/export-preview-model';

import { PreviewChangesView } from './preview-changes-view';
import { PreviewEncodingsView } from './preview-encodings-view';
import { PreviewFileView } from './preview-file-view';

/** Sample sizes offered in the toolbar. The endpoint accepts 1–50; these three
 *  cover "glance", "check a few" and "look for outliers" without inviting a
 *  50-row read inside a drawer. Each size is a separate cached query. */
const ROW_CHOICES = [5, 10, 25];
const DEFAULT_ROWS = 5;

type PreviewTab = 'file' | 'changes' | 'encodings';

/** `request` (the batch export's, template included) or a `fetcher` for another source. */
type ExportPreviewProps = ExportPreviewSource & {
  /** False while the drawer is closed — the preview reads the enrichment table. */
  enabled: boolean;
  /** The chosen template — supplies the per-column encoding map. */
  template: ExportTemplateDefinition | undefined;
  /** Column key → its config entry (label, kind, identifier, spec limits). */
  columnsByKey: Record<string, ExportTemplateColumn>;
  /** Transformation code → definition, for labels and the Encodings tab. */
  transformationsByCode: Record<string, ExportTransformation>;
  /** The vendor feed spec this connector's encodings reproduce. */
  specUrl: string | null;
  /** The selected format — its real delimiter drives copy and download. */
  format: ExportFormatOption | undefined;
  /** Used to name the downloaded sample file. */
  fileLabel: string;
  /** The column selection, for hiding unselected header columns in the File grid. */
  visibleKeys?: ReadonlySet<string>;
  /**
   * Controls the sample-size selector instead of the component's own state —
   * e.g. so a sibling raw-text view can stay in sync and read the same count.
   * Omitted (the default for every existing caller), the row count is owned
   * internally exactly as before.
   */
  rows?: number;
  /** Called on every sample-size change when `rows` is controlled; ignored otherwise. */
  onRowsChange?: (rows: number) => void;
  /** Forwarded to the File view's own `boundedHeight` (see its doc) and, here, lets the tab
   *  panel grow/scroll in place instead of only being as tall as its content — for a caller with
   *  no outer scroller of its own (the catalog builder's tab), as opposed to every existing
   *  caller's `Sheet`, which already owns the page's one scroller. Defaults to `false`. */
  boundedHeight?: boolean;
};

/**
 * The export preview: the run's first products, as the chosen template will
 * write them, in three views the operator switches between.
 *
 * - **File** — the actual output grid. "What will I open in a spreadsheet."
 * - **Changes** — only the columns this template rewrites, stored → exported.
 * - **Encodings** — what each encoding does, with the config's worked examples.
 *
 * The default tab follows the template, because the useful question changes with
 * it: a raw template rewrites nothing, so its Changes tab is empty by
 * definition and File is the only view with anything to say; a feed template is
 * chosen *for* its encodings, so Changes leads.
 *
 * Owns all the vertical room the drawer can spare and is the drawer's only
 * scroll region, so there is no nested-scroll trap between the two.
 */
export function ExportPreview({
  enabled,
  template,
  columnsByKey,
  transformationsByCode,
  specUrl,
  format,
  fileLabel,
  visibleKeys,
  rows: controlledRows,
  onRowsChange,
  boundedHeight = false,
  ...source
}: ExportPreviewProps) {
  const { t } = useTranslation('exports');
  const [internalRows, setInternalRows] = useState(DEFAULT_ROWS);
  // Controlled when the caller passes `rows` (e.g. `CatalogFilePreview`, keeping a raw-text
  // sibling view on the same sample); every existing caller omits it and keeps this state.
  const rows = controlledRows ?? internalRows;
  const setRows = (next: number) => {
    if (controlledRows === undefined) setInternalRows(next);
    onRowsChange?.(next);
  };
  const [productIndex, setProductIndex] = useState(0);
  const [copied, setCopied] = useState(false);

  const { data, isPending, isError, error } = useExportTemplatePreview({
    ...source,
    enabled,
    rows,
  });

  // A raw template's `transformations` delta is `{}`, so this is empty for it and
  // both the Changes and Encodings tabs correctly have nothing to show.
  const encodedKeys = useMemo(
    () => new Set(Object.keys(template?.transformations ?? {})),
    [template],
  );
  const hasEncodings = encodedKeys.size > 0;

  // Which view the operator explicitly picked. `null` means "follow the data",
  // resolved below — never synced in an effect, so there is no render where the
  // tab and the panel disagree.
  const [chosenTab, setChosenTab] = useState<PreviewTab | null>(null);

  // Clamp rather than store-and-sync: the row count arrives with the response,
  // so an index left from a larger sample must not read past the end.
  const rowIndex = data && data.rows.length > 0 ? Math.min(productIndex, data.rows.length - 1) : 0;

  const cells = useMemo(
    () =>
      buildPreviewCells({ preview: data, rowIndex, template, columnsByKey, transformationsByCode }),
    [data, rowIndex, template, columnsByKey, transformationsByCode],
  );
  const changedCells = useMemo(() => cells.filter((cell) => cell.changed), [cells]);

  const transformations = useMemo(
    () =>
      appliedTransformations({
        template,
        columnsByKey,
        transformationsByCode,
        header: data?.header,
      }),
    [template, columnsByKey, transformationsByCode, data?.header],
  );

  // Availability is derived from the response, not from the template's delta: a
  // template can name encodings that happen not to alter any sampled product,
  // and an empty Changes tab is the honest answer in that case too.
  const changesAvailable = changedCells.length > 0;
  const encodingsAvailable = transformations.length > 0;

  /**
   * The view on screen. An explicit pick wins while it still has content;
   * otherwise the lead view follows the data — Changes when this template
   * actually rewrites something, File when it doesn't.
   *
   * Resolved rather than stored, so a choice stranded by a template switch
   * (GMC Feed → Changes → back to Raw) falls back for exactly as long as it is
   * empty and is honoured again if the operator switches back. An effect that
   * nulled it would have thrown the choice away permanently.
   */
  const tabHasContent = (candidate: PreviewTab): boolean =>
    candidate === 'file' || (candidate === 'changes' ? changesAvailable : encodingsAvailable);
  const tab: PreviewTab =
    chosenTab !== null && tabHasContent(chosenTab)
      ? chosenTab
      : changesAvailable
        ? 'changes'
        : 'file';

  const delimitedText = useMemo(
    () =>
      data && format ? previewToDelimitedText({ preview: data, delimiter: format.delimiter }) : '',
    [data, format],
  );

  // Timer that clears the "Copied" confirmation. Held in a ref and cancelled on
  // unmount: closing the drawer within the 2s window would otherwise setState on
  // an unmounted component.
  const copyResetTimer = useRef<number | undefined>(undefined);
  useEffect(
    () => () => {
      window.clearTimeout(copyResetTimer.current);
    },
    [],
  );

  /** Copies the sample as the delimiter-correct text the file would contain, so
   *  it can be pasted straight into a spreadsheet and checked. */
  const handleCopy = () => {
    void navigator.clipboard.writeText(delimitedText).then(
      () => {
        setCopied(true);
        window.clearTimeout(copyResetTimer.current);
        copyResetTimer.current = window.setTimeout(() => {
          setCopied(false);
        }, 2000);
      },
      () => {
        // Clipboard access can be refused (permission, or a non-secure context).
        // Surfaced rather than swallowed — the operator clicked and needs to know
        // nothing was copied, and that Download is the way through.
        setCopied(false);
        toast.error(t('preview.copyFailed'));
      },
    );
  };

  /** Saves the sample as a real file with the format's own extension — the
   *  quickest way to confirm a spreadsheet or the vendor accepts the shape.
   *  Goes through the shared `triggerBrowserDownload`, which appends the anchor
   *  and defers `revokeObjectURL` to the next macrotask; revoking it
   *  synchronously aborts the download in some browsers. */
  const handleDownloadSample = () => {
    if (!data || !format) return;
    const blob = new Blob([delimitedText], { type: format.contentType });
    triggerBrowserDownload(blob, `${fileLabel}-sample${format.extension}`);
  };

  const isEmpty = data?.rows.length === 0;
  const panelId = 'export-preview-panel';

  return (
    <section data-testid="export-preview" className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <SegmentedTabs
          value={tab}
          onChange={setChosenTab}
          panelId={panelId}
          testId="export-preview-tabs"
          ariaLabel={t('preview.tabs.ariaLabel')}
          className="w-auto sm:max-w-none"
          options={[
            { value: 'file', label: t('preview.tabs.file'), tabId: 'export-preview-tab-file' },
            {
              value: 'changes',
              label: t('preview.tabs.changes'),
              tabId: 'export-preview-tab-changes',
              ...(changedCells.length > 0 ? { badge: String(changedCells.length) } : {}),
              // Disabled rather than hidden: the count IS the answer for a raw
              // template, and a tab that appears and vanishes is worse to use.
              disabled: !changesAvailable,
              tooltip: hasEncodings ? t('preview.tabs.noChangesYet') : t('preview.noChangeSummary'),
            },
            {
              value: 'encodings',
              label: t('preview.tabs.encodings'),
              tabId: 'export-preview-tab-encodings',
              ...(transformations.length > 0 ? { badge: String(transformations.length) } : {}),
              disabled: !encodingsAvailable,
              tooltip: t('preview.noChangeSummary'),
            },
          ]}
        />

        <div className="flex flex-wrap items-center gap-1.5">
          {/* Sample size. The endpoint allows up to 50 and `staleTime: Infinity`
              means each size is fetched at most once per session. */}
          <Select
            value={String(rows)}
            onValueChange={(value) => {
              setRows(Number(value));
            }}
          >
            <SelectTrigger
              className="h-8 w-auto cursor-pointer gap-1 text-xs"
              aria-label={t('preview.rowCountAria')}
              data-testid="export-preview-row-count-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent data-testid="export-preview-row-count-options">
              {ROW_CHOICES.map((choice) => (
                <SelectItem
                  key={choice}
                  value={String(choice)}
                  data-testid={`export-preview-row-count-option-${String(choice)}`}
                  className="cursor-pointer text-xs"
                >
                  {t('preview.rowCount', { count: choice })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Product pager — Changes only. The File view already shows every
              sampled product at once, and Encodings is a config reference that
              does not vary by product, so a position readout beside either
              would be meaningless. */}
          {tab === 'changes' && data && data.rows.length > 1 && (
            <div data-testid="export-preview-pager" className="flex items-center gap-0.5">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={cn('h-8 w-8', rowIndex === 0 ? 'cursor-not-allowed' : 'cursor-pointer')}
                disabled={rowIndex === 0}
                aria-label={t('preview.previousProduct')}
                data-testid="export-preview-pager-prev-button"
                onClick={() => {
                  setProductIndex(rowIndex - 1);
                }}
              >
                <ChevronLeft aria-hidden="true" className="h-4 w-4" />
              </Button>
              <span
                data-testid="export-preview-pager-position"
                className="text-muted-foreground px-1 text-xs whitespace-nowrap"
              >
                {t('preview.productPosition', {
                  position: rowIndex + 1,
                  total: data.rows.length,
                })}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={cn(
                  'h-8 w-8',
                  rowIndex >= data.rows.length - 1 ? 'cursor-not-allowed' : 'cursor-pointer',
                )}
                disabled={rowIndex >= data.rows.length - 1}
                aria-label={t('preview.nextProduct')}
                data-testid="export-preview-pager-next-button"
                onClick={() => {
                  setProductIndex(rowIndex + 1);
                }}
              >
                <ChevronRight aria-hidden="true" className="h-4 w-4" />
              </Button>
            </div>
          )}

          {data && data.rows.length > 0 && format && (
            <>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 cursor-pointer"
                aria-label={copied ? t('preview.copied') : t('preview.copy')}
                data-testid="export-preview-copy-button"
                onClick={handleCopy}
              >
                {copied ? (
                  <Check aria-hidden="true" className="text-success h-4 w-4" />
                ) : (
                  <Copy aria-hidden="true" className="h-4 w-4" />
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 cursor-pointer text-xs"
                data-testid="export-preview-download-sample-button"
                onClick={handleDownloadSample}
              >
                <Download aria-hidden="true" className="mr-1 h-4 w-4" />
                {format.extension}
              </Button>
            </>
          )}
        </div>
      </div>

      {isPending && (
        // Content height, matching the loaded views — a `flex-1` skeleton filled
        // the drawer and then collapsed to a five-row table, which reads as a
        // layout jump.
        <div data-testid="export-preview-loading" className="space-y-2">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={`preview-skeleton-${String(i)}`} className="h-9 w-full" />
          ))}
        </div>
      )}

      {isError && (
        <p data-testid="export-preview-error" className="text-error text-sm">
          {extractApiError(error, t('preview.error'))}
        </p>
      )}

      {isEmpty && (
        <p data-testid="export-preview-empty" className="text-muted-foreground text-sm">
          {t('preview.empty')}
        </p>
      )}

      {data && data.rows.length > 0 && (
        <div
          id={panelId}
          data-testid={`export-preview-panel-${tab}`}
          role="tabpanel"
          aria-labelledby={`export-preview-tab-${tab}`}
          // Content height by default, like the views inside it — with `flex-1` there the panel
          // filled the drawer's column and pushed the sample note below it to the bottom,
          // detached from the table it describes. `boundedHeight` callers have no such note
          // competing for room below a short drawer, and no outer scroller either, so they opt
          // into `flex-1`/`overflow-hidden` here to hand that space straight to the table.
          className={cn('flex min-h-0 flex-col', boundedHeight && 'flex-1 overflow-hidden')}
        >
          {/* States what THIS panel is scoped to. The two tabs were showing
              before/after pairs that looked interchangeable; naming the scope —
              this product, versus the template's rules — is what separates
              them, and it belongs on screen rather than in a tooltip. */}
          {tab !== 'file' && (
            <p
              data-testid="export-preview-scope"
              className="text-muted-foreground mb-2 shrink-0 text-xs leading-relaxed"
            >
              {tab === 'changes'
                ? t('preview.scope.changes', {
                    count: changedCells.length,
                    total: data.header.length,
                  })
                : t('preview.scope.encodings', { count: transformations.length })}
            </p>
          )}

          {tab === 'file' && (
            <PreviewFileView
              preview={data}
              columnsByKey={columnsByKey}
              encodedKeys={encodedKeys}
              showSpecWarnings={hasEncodings}
              template={template}
              transformationsByCode={transformationsByCode}
              boundedHeight={boundedHeight}
              {...(visibleKeys ? { visibleKeys } : {})}
            />
          )}
          {tab === 'changes' && (
            <PreviewChangesView cells={changedCells} boundedHeight={boundedHeight} />
          )}
          {tab === 'encodings' && (
            <PreviewEncodingsView
              transformations={transformations}
              specUrl={specUrl}
              boundedHeight={boundedHeight}
            />
          )}
        </div>
      )}

      {data && data.rows.length > 0 && (
        <p
          data-testid="export-preview-sample-note"
          className="text-muted-foreground shrink-0 text-xs"
        >
          {data.truncated
            ? t('preview.truncatedNote', { count: data.rowCount })
            : t('preview.sampleNote', { count: data.rowCount })}
        </p>
      )}
    </section>
  );
}
